<template>
  <OLinkBox
    ref="articleItemRef"
    class="article-card-wrapper"
    :style="maxWidth"
    v-bind="$attrs"
    v-on="dataLayer ? { click: dataLayerClickEvent } : {}"
  >
    <article class="article-card" :class="variantClass">
      <ArticleCardImage :article="article" :size="size" :class="mediaClass" :full-width="fullWidth" />
      <ArticleBadge v-if="hasBadge" pin :badge="article.badges[0]" :title="article.title"></ArticleBadge>
      <div class="card__body w-full">
        <header>
          <component :is="element.component ? element.component : 'h3'" class="sm:font-bold text-primary-100" :class="element.class">
            <OLinkOverlay class="hover:text-link-primary" :href="articleUrl" v-bind="bind" :title="article.title">
              {{ article.title }}
            </OLinkOverlay>
          </component>
        </header>
        <!-- eslint-disable-next-line prettier/prettier vue/no-v-html -->
        <p
          v-if="description && !hide.includes('description')"
          class="article__desc mt-1 text-md text-primary-200 leading-18"
          v-html="description"
        ></p>
        <aside v-if="hasDate && !hide.includes('date')" class="mt-0.5">
          <ArticleCardDate :article="article" />
        </aside>
      </div>
    </article>
  </OLinkBox>
</template>

<script>
import { computed } from '@nuxtjs/composition-api';
import ArticleBadge from './ArticleBadge';
import ArticleCardImage from './ArticleCardImage';
import ArticleCardDate from './ArticleCardDate';
import { articleCardSizes, getArticleDescription } from './articleCardUtils';
import { useTracking } from '~/modules/tracking/composables/useTracking';
import { OLinkOverlay, OLinkBox } from '~/modules/common/components/ui';
import { getArticleUrl } from '~/modules/common/utils/routingUtils';

/**
 * Article Card config
 * @info keep it sync with articleCardSizes
 */
const elementConfig = {
  default: { class: 'text-xl leading-22' },
  defaultMobile: { class: 'text-lg leading-22' },
  sm: { class: 'text-base leading-24' },
  md: { component: 'h5', class: 'text-md leading-18' },
  lg: { component: 'h5', class: 'text-lg leading-20' },
  full: { class: 'text-xl tracking-tight leading-24' },
};

export default {
  components: {
    ArticleBadge,
    ArticleCardImage,
    ArticleCardDate,
    OLinkBox,
    OLinkOverlay,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    hide: {
      type: Array,
      required: false,
      default: () => [],
    },
    size: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'defaultMobile', 'sm', 'md', 'lg', 'full'].includes(value),
    },
    variant: {
      type: String,
      default: 'horizontal',
      validator: (value) => ['vertical', 'horizontal'].includes(value),
    },
    refName: {
      type: String,
      required: false,
    },
    fullWidth: {
      type: Boolean,
      required: false,
    },
    dataLayer: {
      type: Object,
    },
  },
  setup({ variant, size, article, refName, dataLayer }) {
    const variantClasses = { horizontal: 'flex w-full', vertical: 'flex flex-col w-full' };
    const variantClass = computed(() => variantClasses[variant]);

    const mediaClasses = { horizontal: 'mr-2.5', vertical: 'mb-1.5' };
    const mediaClass = computed(() => mediaClasses[variant]);

    const defaultClass = size;
    const element = computed(() => (variant === 'vertical' ? elementConfig[size] : elementConfig[defaultClass]));
    const elementSize = computed(() => (variant === 'vertical' ? articleCardSizes[size] : articleCardSizes[defaultClass]));

    // vertical max-width
    const maxWidth = computed(() => (variant === 'vertical' ? { width: elementSize.value.width } : {}));

    const articleUrl = computed(() => getArticleUrl({ article, query: refName && { ref: refName } }));

    const hasBadge = computed(() => article?.badges?.length);

    const description = getArticleDescription(article);

    const hasDate = computed(() => article.dates?.updated);

    // Data Layer Impression & Click
    const { sendDataLayer } = useTracking();
    let articleItemRef, dataLayerClickEvent;
    if (dataLayer) {
      const dataLayerObject = sendDataLayer(
        {
          item: article,
          list: dataLayer.list,
          position: dataLayer.position,
        },
        ['scroll', 'click']
      );
      articleItemRef = dataLayerObject.elRef;
      dataLayerClickEvent = dataLayerObject.clickEvent;
    }

    const bind = {};
    if (article?.redirectUrl) {
      bind.target = '_blank';
      bind.rel = 'noopener noreferrer nofollow';
    }

    return {
      articleUrl,
      hasBadge,
      variantClass,
      mediaClass,
      maxWidth,
      element,
      elementSize,
      description,
      hasDate,
      bind,
      articleItemRef,
      dataLayerClickEvent,
    };
  },
};
</script>
